import {
  BudgetGoalSchema,
  BudgetItemSchema,
  BudgetItemType,
  createBudgetGoalSchema,
  CreateBudgetGoalSchema,
  CreateBudgetItemSchema,
  goalSchema,
  historicalInvoiceSchema,
  HistoricalInvoiceSchema,
  objectiveBudgetItemSchema,
  ObjectiveBudgetItemSchema
} from './schemas'
import { getWillieSupabaseClient } from '~/utils'
import { TablesInsert } from '@willie/supabase'
import _ from 'lodash'

type ListSiteBudgetItemsProps<T extends BudgetItemType> = {
  siteID: string
  type: T
}

type ListSiteBudgetItemReturn<T extends BudgetItemType> = T extends 'historical'
  ? HistoricalInvoiceSchema
  : ObjectiveBudgetItemSchema

export async function listSiteBudgetItems<T extends BudgetItemType>({
  siteID,
  type
}: ListSiteBudgetItemsProps<T>): Promise<ListSiteBudgetItemReturn<T>[]> {
  const supabase = getWillieSupabaseClient()

  const { data, error } = await supabase
    .from('site_budget_items')
    .select('*')
    .eq('site_id', siteID)
    .eq('type', type)

  if (error) {
    throw error
  }

  const schema = type === 'historical' ? historicalInvoiceSchema : objectiveBudgetItemSchema

  const d = data.map(
    (d) =>
      schema.parse({
        id: d.id,
        siteID: d.site_id,
        startDate: d.start_date,
        endDate: d.end_date,
        consumption: d.consumption,
        cost: d.total_cost,
        type: d.type
      }) as ListSiteBudgetItemReturn<T>
  )
  return d
}

export const createBudgetItem = async (data: CreateBudgetItemSchema) => {
  const supabase = getWillieSupabaseClient()

  const payload: TablesInsert<'site_budget_items'> = {
    site_id: data.siteID,
    start_date: data.startDate.toISOString(),
    end_date: data.endDate.toISOString(),
    consumption: data.consumption,
    total_cost: data.cost,
    type: data.type
  }

  const { error } = await supabase.from('site_budget_items').insert([payload])

  if (error) {
    throw error
  }
}

export const updateBudgetItem = async (data: BudgetItemSchema) => {
  const supabase = getWillieSupabaseClient()

  const payload: Partial<TablesInsert<'site_budget_items'>> = {
    ...(data.startDate && { start_date: data.startDate.toISOString() }),
    ...(data.endDate && { end_date: data.endDate.toISOString() }),
    ...(data.consumption && { consumption: data.consumption }),
    ...(data.cost && { total_cost: data.cost })
  }

  const { error } = await supabase.from('site_budget_items').update(payload).eq('id', data.id)

  if (error) {
    throw error
  }
}

const mapGoalSchemaToSupabase = (
  data: BudgetGoalSchema | CreateBudgetGoalSchema
): TablesInsert<'budget_goals'> => {
  return {
    start_date: data.startDate.toISOString(),
    end_date: data.endDate.toISOString(),
    goal: data.goal,
    measure_type: data.measure,
    metric_id: data.metricID ?? null,
    station_id: data.stationID ?? null,
    resolution_period: data.resolution
  }
}

const mapSupabaseToGoalSchema = (data: any): BudgetGoalSchema => {
  return goalSchema.parse({
    id: data.id,
    startDate: data.start_date,
    endDate: data.end_date,
    goal: data.goal,
    measure: data.measure_type,
    resolution: data.resolution_period,
    ...(data.metric_id &&
      !!data.metric && { metricID: data.metric_id, metric: { id: data.metric_id, name: data.metric.name } }),
    ...(data.station_id &&
      !!data.station && {
        stationID: data.station_id,
        station: { id: data.station_id, name: data.station.name }
      })
  })
}

export const deleteBudgetItem = async (id: string) => {
  const supabase = getWillieSupabaseClient()

  const { error } = await supabase.from('site_budget_items').delete().eq('id', id)

  if (error) {
    throw error
  }
}

type ListBudgetGoalsFilter = {
  siteID?: string
  metricID?: string
  stationID?: string
}

export const listBudgetGoals = async (filter?: ListBudgetGoalsFilter) => {
  const supabase = getWillieSupabaseClient()
  let query = supabase.from('budget_goals').select(`
    *,
    metric:metrics(id, name, site_id),
    station:water_stations(id, name, site_id)
  `)

  // Apply filters if provided
  if (filter?.metricID) {
    query = query.eq('metric_id', filter.metricID)
  } else if (filter?.stationID) {
    query = query.eq('station_id', filter.stationID)
  }

  const { data, error } = await query.order('start_date', { ascending: true })

  if (error) {
    console.error('Error executing listBudgetGoals', error)
    throw error
  }

  const goals = _(data)
    .filter(
      (goal) =>
        !filter?.siteID || goal.metric?.site_id === filter.siteID || goal.station?.site_id === filter.siteID
    )
    .map((goal) => mapSupabaseToGoalSchema(goal))
    .value()

  return goals
}

export const getBudgetGoal = async (id: string) => {
  const supabase = getWillieSupabaseClient()
  const { data, error } = await supabase
    .from('budget_goals')
    .select(`*,metric:metrics(id, name, site_id),station:water_stations(id, name, site_id)`)
    .eq('id', id)
    .single()

  if (error) {
    console.error(' Error executing getBudgetGoal', error)
    throw error
  }

  return mapSupabaseToGoalSchema(data)
}

export const createBudgetGoal = async (goal: CreateBudgetGoalSchema) => {
  const parsed = createBudgetGoalSchema.parse(goal)

  const supabase = getWillieSupabaseClient()
  const payload = mapGoalSchemaToSupabase(parsed)
  const { data, error } = await supabase.from('budget_goals').insert([payload]).select().single()

  if (error) {
    console.error(' Error executing createBudgetGoal', error)
    throw error
  }
  return mapSupabaseToGoalSchema(data)
}

export const updateBudgetGoal = async (data: BudgetGoalSchema) => {
  const supabase = getWillieSupabaseClient()
  const payload = mapGoalSchemaToSupabase(data)

  const { error } = await supabase.from('budget_goals').update(payload).eq('id', data.id)

  if (error) {
    console.error(' Error executing udpdateBudgetGoal', error)
    throw error
  }
}

export const deleteBudgetGoal = async (goalID: string) => {
  const supabase = getWillieSupabaseClient()
  const { error } = await supabase.from('budget_goals').delete().eq('id', goalID)

  if (error) {
    console.error(' Error executing deleteBudgetGoal', error)
    throw error
  }
}
